import React from 'react';
import { ArrowRight } from 'lucide-react';
import { AllodosLogo } from './AllodosLogo';
import type { BookingConfig, BookingProfessional } from '../services/bookingApi';

/**
 * Bloco 35: a página pública (PatientPortal) e a pré-visualização em
 * "Configuração página de agendamento" renderizam estes mesmos componentes,
 * para que o editor mostre exatamente o que o paciente vê no link externo.
 */
export type LandingClinic = NonNullable<BookingConfig['clinic']> | null | undefined;
export type LandingAppearance = BookingConfig['appearance'] | null | undefined;

/** Fundo do painel lateral: gradiente (ou imagem) configurado no editor. */
export function landingBackgroundStyle(a: LandingAppearance): React.CSSProperties {
  if (!a) return { background: 'linear-gradient(135deg, #6366f1, #a855f7)' };
  if (a.backgroundType === 'image' && a.imageUrl) {
    return { backgroundImage: `url(${a.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' };
  }
  return { background: `linear-gradient(135deg, ${a.gradientFrom || '#6366f1'}, ${a.gradientTo || '#a855f7'})` };
}

/** Bloco #69: só aceita #RGB ou #RRGGBB (valor inválido não quebra a página). */
export const isHexColor = (value?: string | null): value is string => !!value && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());

/** Fonte das informações complementares (família, tamanho, negrito, itálico, sublinhado). */
export function complementaryTextStyle(a: LandingAppearance): React.CSSProperties {
  if (!a) return {};
  return {
    fontFamily: a.fontFamily || undefined,
    fontSize: a.fontSize || undefined,
    fontWeight: a.bold ? 'bold' : undefined,
    fontStyle: a.italic ? 'italic' : undefined,
    textDecoration: a.underline ? 'underline' : undefined,
  };
}

export const BookingLandingAside: React.FC<{
  clinic: LandingClinic;
  appearance: LandingAppearance;
  /** Versão reduzida para a pré-visualização dentro do editor. */
  compact?: boolean;
}> = ({ clinic, appearance, compact = false }) => {
  const pad = compact ? 'p-5' : 'p-5 sm:p-8 md:p-12';
  // Imagem de fundo recebe um véu escuro proporcional à "transparência" para o
  // texto continuar legível; o gradiente é usado como está.
  const veil =
    appearance?.backgroundType === 'image' && appearance.imageUrl
      ? { background: `rgba(15, 23, 42, ${Math.min(0.85, (Number(appearance.transparency ?? 0) / 100) * 0.6)})` }
      : null;
  // Bloco #68: "Vazio" esconde título, mensagens, links e rodapé — fica só o
  // fundo. O conteúdo digitado continua salvo e volta no "Texto personalizado".
  const blank = appearance?.sideContent === 'blank';
  // Bloco #69: cor do texto escolhida no editor (padrão: branco).
  const textColor = isHexColor(appearance?.textColor) ? appearance!.textColor!.trim() : '#ffffff';
  return (
    <aside
      className={`relative ${compact ? 'md:w-[38%]' : 'md:w-[38%]'} ${pad} ${blank ? (compact ? 'min-h-[160px]' : 'min-h-[220px]') : ''} flex flex-col justify-between overflow-hidden`}
      style={{ ...landingBackgroundStyle(appearance), color: textColor }}
      aria-label={blank ? 'Imagem de fundo' : undefined}
    >
      {veil && <div className="absolute inset-0 pointer-events-none" style={veil} />}
      {!blank && (
        <>
          <div className={`relative ${compact ? 'space-y-4' : 'space-y-6'}`}>
            <div className={compact ? 'w-28' : 'w-44'}>
              <AllodosLogo variant="white" className="w-full h-auto" />
            </div>
            <div>
              <h1 className={`${compact ? 'text-lg' : 'text-2xl md:text-3xl'} font-black tracking-tight`}>
                {clinic?.name || 'Agende sua consulta'}
              </h1>
              {clinic?.welcomeMessage && (
                <p className={`${compact ? 'text-[11px] mt-2' : 'text-sm mt-3'} leading-relaxed opacity-85 font-medium`}>
                  {clinic.welcomeMessage}
                </p>
              )}
            </div>

            {clinic?.complementaryText && (
              <p
                className={`${compact ? 'text-[10px]' : 'text-xs'} leading-relaxed opacity-75 whitespace-pre-wrap break-words`}
                style={complementaryTextStyle(appearance)}
              >
                {clinic.complementaryText}
              </p>
            )}

            {!!clinic?.links?.length && (
              <div className={`space-y-2 ${compact ? 'pt-1' : 'pt-2'}`}>
                {clinic.links.map((l) => (
                  <a
                    key={`${l.label}-${l.url}`}
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                    className={`block ${compact ? 'text-[9px] px-3 py-2' : 'text-xs px-4 py-3'} font-black uppercase tracking-widest text-white bg-white/15 hover:bg-white/25 transition-colors rounded-2xl`}
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            )}
          </div>
          <p className={`relative ${compact ? 'text-[8px] mt-6' : 'text-[10px] mt-10'} font-black uppercase tracking-[0.25em] opacity-60`}>
            Agendamento online
          </p>
        </>
      )}
    </aside>
  );
};

/** Fotos de banco de imagens que o sistema usava como padrão — não são do profissional. */
export const isStockPhoto = (url?: string | null): boolean => !url || /images\.unsplash\.com/.test(url);

/**
 * Bloco #61: foto do profissional ou, sem foto, avatar neutro com as iniciais
 * (antes aparecia a foto de banco de imagens de outra pessoa).
 */
export const ProfessionalAvatar: React.FC<{ name: string; photo?: string | null; className?: string }> = ({ name, photo, className = 'w-14 h-14 rounded-2xl' }) => {
  if (photo && !isStockPhoto(photo)) {
    return <img src={photo} alt={name} className={`${className} object-cover shrink-0`} />;
  }
  const initials = (name || '')
    .split(/\s+/)
    .filter((w) => /^\p{L}/u.test(w))
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
  return (
    <div aria-hidden className={`${className} shrink-0 flex items-center justify-center bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300 font-black text-xs`}>
      {initials || '•'}
    </div>
  );
};

export const ProfessionalCard: React.FC<{
  prof: Pick<BookingProfessional, 'id' | 'name' | 'formation' | 'crp' | 'photo' | 'bio'>;
  onSelect: () => void;
  compact?: boolean;
}> = ({ prof, onSelect, compact = false }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`w-full flex items-center gap-4 ${compact ? 'p-3.5 rounded-2xl' : 'p-5 rounded-[1.5rem]'} bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-indigo-400 transition-all text-left shadow-sm`}
  >
    <ProfessionalAvatar name={prof.name} photo={prof.photo} className={compact ? 'w-10 h-10 rounded-xl' : 'w-14 h-14 rounded-2xl'} />
    <div className="min-w-0 flex-1">
      <p className={`font-black ${compact ? 'text-xs' : 'text-sm'} text-slate-900 dark:text-white`}>{prof.name}</p>
      <p className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-slate-500 font-bold`}>{prof.formation}</p>
      <p className={`${compact ? 'text-[9px]' : 'text-[10px]'} text-slate-400 font-mono`}>{prof.crp}</p>
      {prof.bio && !compact && <p className="text-[11px] text-slate-400 mt-1 line-clamp-2">{prof.bio}</p>}
    </div>
    <span className={`shrink-0 inline-flex items-center gap-1 ${compact ? 'px-3 py-1.5 text-[9px]' : 'px-4 py-2 text-[10px]'} bg-indigo-600 text-white rounded-xl font-black uppercase tracking-widest`}>
      Agendar <ArrowRight size={compact ? 10 : 12} />
    </span>
  </button>
);
